import type { AudioProfile } from "@/lib/perf/perf-profile";

/**
 * Timbre du piano, avant les effets.
 *
 * Les échantillons Salamander sont pris au micro près des cordes : brillants,
 * un peu maigres du bas. Trois filtres leur rendent le corps d'un queue de
 * concert entendu depuis la salle, et surtout le poids d'un instrument qui
 * pousse l'air :
 *
 * - une étagère grave basse et large : le grave descend plus bas qu'une
 *   remontée du haut-grave, qui n'aurait fait qu'épaissir ;
 * - un creux léger dans le bas-médium, là où un piano sonne « carton » : sans
 *   lui, la bosse de grave rend le registre du milieu pâteux et la main
 *   gauche perd ses contours ;
 * - une étagère aiguë discrète : le marteau recule sans éteindre l'attaque.
 *
 * Le gain de sortie compense la bosse de grave : sans lui, un accord grave
 * plaqué à pleine vélocité sature la sortie.
 */
export const PIANO_TONE = {
  /** Étagère grave : +dB sous `bassHz`, le corps de l'instrument. */
  bassGainDb: 6.5,
  bassHz: 150,
  /** Creux du bas-médium : −dB autour de `mudHz`, le « carton » en moins. */
  mudGainDb: -2.5,
  mudHz: 480,
  mudQ: 0.9,
  /** Étagère aiguë : −dB au-dessus de `trebleHz`, le marteau qui recule. */
  trebleGainDb: -2.5,
  trebleHz: 3600,
  /** Rattrapage de niveau, pour que la bosse de grave n'écrête pas. */
  makeup: 0.72,
} as const;

/**
 * Le compresseur, et c'est lui qui rend le son prenant.
 *
 * L'égaliseur donne le poids ; ce qui fait qu'un son *attrape*, c'est la
 * densité — l'écart entre la crête d'attaque et la tenue qui se resserre. Une
 * attaque de 8 ms laisse passer le marteau (le couper tue la frappe), puis le
 * gain se referme et tient la note pendant qu'elle décroît. Un rapport
 * modéré : au-delà, la nuance du jeu s'aplatit, et sur un piano c'est tout ce
 * qui reste du joueur.
 *
 * C'est un `DynamicsCompressorNode` natif, quelques microsecondes par tampon.
 */
export const PIANO_GLUE = {
  thresholdDb: -20,
  ratio: 3,
  attack: 0.008,
  release: 0.22,
  kneeDb: 12,
  /**
   * Rattrapage du gain mangé par la compression.
   *
   * Réglé sur un rendu hors-ligne du pire cas : six notes graves à vélocité
   * maximale, frappées deux fois. Brut, ces six notes écrêtent déjà (crête
   * 2,23). À travers la chaîne, crête 0,96 et rien au-dessus de 1 — et
   * surtout une crête sur cinq fois et demie la valeur efficace, contre dix
   * fois et demie à nu : c'est ce resserrement qu'on entend comme de la
   * présence. Au-delà de 1,4, le même accord repasse au-dessus de 1.
   */
  makeup: 1.35,
} as const;

/**
 * Tone.js audio-fx bus shared by every Salamander/Sampler synth. Owns the
 * tone shaping, the dry/wet routing and the optional reverb so synth
 * implementations can stay ignorant of the effect chain — they connect their
 * sampler output to the chain and that's it.
 *
 * `Tone` is passed in (rather than `import`-ed) so the synth that owns the
 * dynamic Tone import can share it without forcing a second module-load.
 */
export class AudioFxChain {
  private bass: any;
  private mud: any;
  private treble: any;
  private glue: any;
  private trim: any;
  private dryGain: any;
  private wetGain: any | null = null;
  private reverb: any | null = null;

  constructor(private readonly Tone: any, private readonly profile: AudioProfile, reverbMix: number) {
    const wetMix = profile.enableReverb ? reverbMix : 0;

    this.dryGain = new Tone.Gain(1 - wetMix);
    this.dryGain.toDestination();

    if (profile.enableReverb) {
      this.wetGain = new Tone.Gain(wetMix);
      this.wetGain.toDestination();
      // Une salle qui répond sans éloigner : la queue reste longue, mais le
      // dosage (`reverbMix`) garde l'instrument devant. Au-delà, on gagne de
      // l'espace et on perd la prise.
      this.reverb = new Tone.Reverb({ decay: 2.8, preDelay: 0.02 });
      this.reverb.connect(this.wetGain);
    }

    // Les sources entrent ici : grave → bas-médium → aigu → compresseur →
    // rattrapage → sec/réverbéré. L'égaliseur passe avant le compresseur,
    // sans quoi la bosse de grave déclencherait à elle seule la compression
    // et écraserait le reste à chaque note basse.
    this.bass = new Tone.Filter({
      type: "lowshelf",
      frequency: PIANO_TONE.bassHz,
      gain: PIANO_TONE.bassGainDb,
    });
    this.mud = new Tone.Filter({
      type: "peaking",
      frequency: PIANO_TONE.mudHz,
      gain: PIANO_TONE.mudGainDb,
      Q: PIANO_TONE.mudQ,
    });
    this.treble = new Tone.Filter({
      type: "highshelf",
      frequency: PIANO_TONE.trebleHz,
      gain: PIANO_TONE.trebleGainDb,
    });
    this.glue = new Tone.Compressor({
      threshold: PIANO_GLUE.thresholdDb,
      ratio: PIANO_GLUE.ratio,
      attack: PIANO_GLUE.attack,
      release: PIANO_GLUE.release,
      knee: PIANO_GLUE.kneeDb,
    });
    this.trim = new Tone.Gain(PIANO_TONE.makeup * PIANO_GLUE.makeup);

    this.bass.connect(this.mud);
    this.mud.connect(this.treble);
    this.treble.connect(this.glue);
    this.glue.connect(this.trim);
    this.trim.connect(this.dryGain);

    if (this.reverb) {
      this.trim.connect(this.reverb);
    }
  }

  /** Connects a Tone source (sampler, synth, …) to the tone stage. */
  connectSource(source: { connect(node: any): void }): void {
    source.connect(this.bass);
  }

  dispose(): void {
    this.bass?.dispose();
    this.mud?.dispose();
    this.treble?.dispose();
    this.glue?.dispose();
    this.trim?.dispose();
    this.reverb?.dispose();
    this.wetGain?.dispose();
    this.dryGain?.dispose();
    this.bass = null;
    this.mud = null;
    this.treble = null;
    this.glue = null;
    this.trim = null;
    this.reverb = null;
    this.wetGain = null;
    this.dryGain = null;
  }
}
